/**
 * 日常工作页面 - 参考知识管理界面布局
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Mic, MicOff, Send, Trash2, ChevronLeft, ChevronRight,
  Building2, User, MapPin, Calendar, Target,
  AlertCircle, CheckCircle2, Loader2, Edit3, Eye, X, FileText, Save,
  Search, Check, RotateCcw, Download, Archive, Settings,
} from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import type { VisitRecord, VisitInfoExtracted, VisitRecordCreate } from '../types/api';
import styles from './WorkRecord.module.css';

const PAGE_SIZE = 8;

// 全部字段配置（事前+事后），按阶段分组标记
const PHASE_HINT: Record<string, string> = {};

const FIELD_CONFIG: { key: string; label: string; icon: React.ReactNode; type?: string }[] = [
  { key: 'area', label: '拜访地点', icon: <MapPin size={16} /> },
  { key: 'customer_name', label: '拜访对象名称', icon: <Building2 size={16} /> },
  { key: 'linkman', label: '联系人', icon: <User size={16} /> },
  { key: 'visit_time', label: '拜访时间', icon: <Calendar size={16} />, type: 'date' },
  { key: 'visit_purpose', label: '拜访目的', icon: <Target size={16} />, type: 'textarea' },
  { key: 'visit_result', label: '沟通结果', icon: <CheckCircle2 size={16} />, type: 'textarea' },
  { key: 'remark', label: '备注', icon: <Edit3 size={16} />, type: 'textarea' },
];

const emptyForm = (): VisitRecordCreate => ({
  area: '', customer_name: '', linkman: '',
  visit_time: '', visit_purpose: '', visit_result: '', remark: '',
  record_phase: 0,
});

const SECTION_CONFIG = {
  basic: { title: '基础信息', keys: ['area','customer_name','linkman','visit_time'] },
  pre: { title: '事前信息', keys: ['visit_purpose'] },
  post: { title: '事后信息', keys: ['visit_result','remark'] },
};

function FormFields({ data, onChange, readOnly }: { data: Record<string, any>; onChange?: (k: string, v: any) => void; readOnly?: boolean }) {
  const renderField = (key: string, label: string, icon: React.ReactNode, type?: string) => {
    const value = data[key] ?? '';
    return (
      <div key={key} className={`${styles.formField} ${type === 'textarea' ? styles.fullWidth : ''}`}>
        {label ? <label className={styles.fieldLabel}>{icon} {label}</label> : null}
        {readOnly ? <div className={styles.readOnly}>{key === 'visit_time' ? (data.visit_date || '-') : (value || '-')}</div>
        : type === 'textarea' ? (
          <textarea className={styles.fieldTextarea} value={value as string} rows={2} onChange={e => onChange?.(key, e.target.value)} />
        ) : type === 'date' ? (
          <input type="date" className={styles.fieldInput} value={value as string} onChange={e => onChange?.(key, e.target.value)} />
        ) : type === 'number' ? (
          <input type="number" className={styles.fieldInput} value={value as number} min={0} step={0.01} onChange={e => onChange?.(key, e.target.value ? Number(e.target.value) : null)} />
        ) : (
          <input type="text" className={styles.fieldInput} value={value as string} onChange={e => onChange?.(key, e.target.value)} />
        )}
      </div>
    );
  };
  const fieldMap = Object.fromEntries(FIELD_CONFIG.map(f => [f.key, f]));
  return (
    <table style={{width:'100%',borderCollapse:'collapse'}}>
      <tbody>
        {FIELD_CONFIG.map(f => (
          <tr key={f.key} style={{borderBottom:'1px solid #f0f0f0'}}>
            <td style={{width:80,padding:'12px 8px 12px 0',verticalAlign:'middle',fontSize:'.8rem',fontWeight:500,color:'var(--ink-500)',whiteSpace:'nowrap'}}>{f.label}</td>
            <td style={{padding:0,verticalAlign:'middle'}}>{renderField(f.key, '', null, f.type)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function WorkRecord() {
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.user_id;
  const isSuperAdmin = currentUser.role_id === 8 || currentUser.role_name === '超级管理员';

  // 移动端：工具栏折叠 + 卡片展开
  const [showMobileToolbar, setShowMobileToolbar] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const toggleCard = (id: number) => setExpandedCards(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  // 检测微信浏览器
  const isWechat = /micromessenger/i.test(navigator.userAgent);

  const [allRecords, setAllRecords] = useState<VisitRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterSalesman, setFilterSalesman] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');  // 'all'=未归档, 'archived'=已归档, 0/1/2=按阶段

  // 批量下载：勾选要导出的已完成记录
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const STORAGE_KEY_EXTRACT = 'work_extract_fields';
  const loadExtractFields = (): Set<string> => { try { const v = localStorage.getItem(STORAGE_KEY_EXTRACT); if (v) { const arr = JSON.parse(v); if (Array.isArray(arr) && arr.length > 0) return new Set(arr); } } catch {} return new Set(FIELD_CONFIG.map(f => f.key)); };
  const [extractFields, _setExtractFields] = useState<Set<string>>(loadExtractFields);
  const setExtractFields = (s: Set<string> | ((prev: Set<string>) => Set<string>)) => { _setExtractFields(prev => { const next = typeof s === 'function' ? s(prev) : s; try { localStorage.setItem(STORAGE_KEY_EXTRACT, JSON.stringify([...next])); } catch {} return next; }); };


  const [showInputModal, setShowInputModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);  // 前往确认中
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<VisitRecordCreate>(emptyForm());
  const [viewRecord, setViewRecord] = useState<VisitRecord | null>(null);
  const [editRecordId, setEditRecordId] = useState<number | null>(null);
  const [draftVisitId, setDraftVisitId] = useState<number | null>(null);
  const archivePendingRef = useRef(false);  // AI 识别到需要归档，等提交后执行
  const draftRef = useRef<number | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const extractRef = useRef<() => void>(() => {});
  useEffect(() => { extractRef.current = handleExtract; });  // 始终保持最新引用
  const [cumulativeText, setCumulativeText] = useState('');  // 累积多轮讲述的完整文本
  const [extractRound, setExtractRound] = useState(0);         // AI 提取轮次

  // 语音转写完成后自动解析
  useEffect(() => {
    if (!transcribing && transcript.trim() && !extracting) {
      const timer = setTimeout(() => extractRef.current(), 300);
      return () => clearTimeout(timer);
    }
  }, [transcribing]);
  const [histories, setHistories] = useState<any[]>([]);
  const [deletedHistories, setDeletedHistories] = useState<any[]>([]);  // 软删除备份（用于提交时删 DB）
  const deletedIdsRef = useRef<Set<number>>(new Set());  // 读最新值，避免闭包问题
  const [viewHistory, setViewHistory] = useState<any>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);  // 缺失的必填字段
  const PRE_REQUIRED = ['customer_name', 'linkman', 'visit_purpose'];  // 事前三要素
  const REQUIRED_LABELS: Record<string, string> = {
    customer_name: '拜访对象', linkman: '联系人', visit_purpose: '拜访目的',
  };
  const MISSING_HINT = (f: string) => REQUIRED_LABELS[f] || f;
  const getPhase = (data: Record<string, any>): 0 | 1 | 2 => {
    const preDone = PRE_REQUIRED.every(f => data[f] && String(data[f]).trim());
    const postDone = !!(data.visit_result && String(data.visit_result).trim());
    if (!preDone) return 0;
    if (!postDone) return 1;
    return 2;
  };
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getVisitRecords({ page: 1, page_size: 100 }); setAllRecords(res.data); }
    catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { loadRecords(); window.scrollTo(0, 0); }, [loadRecords]);

  const filteredRecords = useMemo(() => {
    const result = allRecords.filter(r => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (![r.customer_name, r.linkman, r.visit_purpose, r.visit_result, r.area, r.salesman].some(v => v?.toLowerCase().includes(q))) return false;
      }
      if (filterPhase === 'archived') { if (!r.is_archived) return false; }
      else if (r.is_archived) return false;  // 未归档标签页排除已归档记录
      if (filterPhase !== 'all' && filterPhase !== 'archived' && String((r as any).record_phase) !== filterPhase) return false;
      if (filterSalesman !== 'all' && r.salesman !== filterSalesman) return false;
      if (filterStartDate && r.visit_date < filterStartDate) return false;
      if (filterEndDate && r.visit_date > filterEndDate) return false;
      return true;
    });
    // 未归档与已归档统一按拜访日期倒序（最新拜访在前）
    result.sort((a, b) => b.visit_date.localeCompare(a.visit_date));
    return result;
  }, [allRecords, searchQuery, filterSalesman, filterPhase, filterStartDate, filterEndDate]);

  const paginatedRecords = filteredRecords;

  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterSalesman, filterPhase, filterStartDate, filterEndDate]);

  const salesmanList = useMemo(() => [...new Set(allRecords.map(r => r.salesman))].sort(), [allRecords]);

  const hasActiveFilters = searchQuery || filterSalesman !== 'all' || filterPhase !== 'all' || filterStartDate || filterEndDate;
  const resetFilters = () => { setSearchQuery(''); setFilterSalesman('all'); setFilterPhase('all'); setFilterStartDate(''); setFilterEndDate(''); };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size === 0) return;
        setTranscribing(true);
        try {
          const res = await api.transcribeAudio(new File([blob], 'recording.webm', { type: 'audio/webm' }));
          setTranscript(prev => prev + (prev ? '\n' : '') + (res.data?.text || ''));
          showToast('success', '语音转写完成');
        } catch (err) { showToast('error', getErrorMessage(err)); }
        finally { setTranscribing(false); }
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err: any) {
      const isHTTP = location.protocol === 'http:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1';
      if (isHTTP) {
        showToast('error', '麦克风需要 HTTPS 连接，请在浏览器地址栏将 http 改为 https 后重试');
      } else if (err?.name === 'NotAllowedError' || err?.message?.includes('Permission')) {
        showToast('error', '麦克风权限被拒绝，请在浏览器设置中允许访问麦克风');
      } else if (err?.name === 'NotFoundError') {
        showToast('error', '未检测到麦克风设备');
      } else {
        showToast('error', '无法访问麦克风：' + (err?.message || '未知错误'));
      }
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  // 从历史记录列表合并所有提取数据（旧→新遍历，最新值覆盖旧值，跳过已标记删除的）
  const mergeHistories = (items: any[]): Record<string, any> => {
    const result: Record<string, any> = {};
    // API 返回 desc(create_time) 最新在前，需反转为旧→新顺序遍历
    for (const item of [...items].reverse()) {
      if (item.raw_data && typeof item.raw_data === 'object') {
        for (const [k, v] of Object.entries(item.raw_data)) {
          if (v !== null && v !== undefined && v !== '') result[k] = v;
        }
      }
    }
    return result;
  };

  // 实时合并结果（用于动态提示）
  const mergedFromHistories = useMemo(() => mergeHistories(histories), [histories]);

  // 检查必填字段并更新缺失列表
  const checkRequired = (data: Record<string, any>) => {
    return PRE_REQUIRED.filter(f => !data[f] || (typeof data[f] === 'string' && !data[f].trim()));
  };

  const handleExtract = async () => {
    if (!transcript.trim()) { showToast('warning', '请输入或语音录入拜访描述'); return; }
    const inputText = transcript.trim();
    setExtracting(true);
    try {
      // 只用本轮独立文本调 AI（不累积）
      const res = await api.extractVisitInfo({ text: inputText, fields: [...extractFields] });
      const d = res.data;
      // 过滤出本轮真正提取到的非空字段（排除内部标记）
      const thisRound: Record<string, any> = {};
      for (const [k, v] of Object.entries(d)) {
        if (k === 'should_archive') continue;  // 不存入历史记录
        if (v !== null && v !== undefined && v !== '') thisRound[k] = v;
      }

      // 标记归档意图
      if (d.should_archive) {
        archivePendingRef.current = true;
      }

      if (Object.keys(thisRound).length === 0) {
        if (d.should_archive) {
          showToast('success', '✅ 已标记为归档');
          setTranscript('');
          setTimeout(() => { setShowInputModal(false); setShowConfirmModal(true); }, 600);
        } else {
          showToast('warning', '未识别到关键信息，请补充更多描述后重试');
        }
        return;
      }

      // 生成本轮摘要（提取的字段名 + 值前15字）
      const fieldLabels = thisRound
        ? Object.entries(thisRound).map(([k, v]) => {
            const label = FIELD_CONFIG.find(f => f.key === k)?.label || k;
            const val = typeof v === 'string' ? v.slice(0, 15) : String(v).slice(0, 15);
            return `${label}:${val}`;
          }).join(', ')
        : `第${histories.length + 1}次录入`;

      // 关联 ID：新建用草稿 visit_id，补录用 editRecordId
      const ctxId = draftRef.current || editRecordId;
      if (!ctxId) { showToast('error', '草稿未创建，请关闭弹窗重新打开'); return; }
      await api.request('/visit-records/history', { method: 'POST', body: JSON.stringify({
        raw_data: thisRound,
        full_text: inputText,
        visit_id: ctxId,
        summary: fieldLabels,
      }) });

      // 刷新历史列表（排除已软删除的条目）
      const r = await api.request<any>('/visit-records/history?visit_id=' + ctxId);
      const freshItems = (r.data || []).filter((item: any) => !deletedIdsRef.current.has(item.id));
      setHistories(freshItems);

      // 合并到 formData（每轮提取立即填入表单）
      const merged = mergeHistories(freshItems);
      setFormData(prev => ({
        ...prev,
        area: merged.area || prev.area || '', customer_name: merged.customer_name || prev.customer_name || '',
        linkman:merged.linkman || prev.linkman || '',
        visit_time: merged.visit_time || prev.visit_time || '', visit_purpose: merged.visit_purpose || prev.visit_purpose || '',
                visit_result: merged.visit_result || prev.visit_result || '',
        remark: merged.remark || prev.remark || '',
        record_phase: getPhase({...prev, ...merged}),
      }));

      // 清空输入
      setTranscript('');

      // 检查缺失字段
      const missing = checkRequired(merged);
      setMissingFields(missing);

      const extractedNames = Object.keys(thisRound).map(k => FIELD_CONFIG.find(f => f.key === k)?.label || k).join('、');
      if (missing.length === 0) {
        showToast('success', `✅ 已提取：${extractedNames}`);
        setTimeout(() => { setShowInputModal(false); setShowConfirmModal(true); }, 800);
      } else {
        const labels = missing.map(f => REQUIRED_LABELS[f] || f).join('、');
        showToast('warning', `已提取：${extractedNames}。还缺少：${labels}`);
      }
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setExtracting(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const phase = getPhase(formData);
      const payload = { ...formData, record_phase: phase };
      if (editRecordId) {
        // 补录事后：更新已有记录
        await api.updateVisitRecord(editRecordId, payload);
        showToast('success', '记录已完成');
      } else if (draftRef.current) {
        // 更新草稿为正式记录（不创建新记录，避免重复）
        await api.updateVisitRecord(draftRef.current, payload);
        showToast('success', '创建成功');
      } else {
        // 兜底：无草稿时创建新记录
        await api.createVisitRecord(payload);
        showToast('success', '创建成功');
      }
      // 实际删除被标记的历史记录
      for (const h of deletedHistories) {
        api.request('/visit-records/history/' + h.id, {method:'DELETE'}).catch(() => {});
      }
      // AI 标记的归档意图：提交后执行
      if (archivePendingRef.current) {
        const targetId = editRecordId || draftRef.current;
        if (targetId) api.toggleArchive(targetId).then(() => showToast('success', '已自动归档')).catch(() => {});
        archivePendingRef.current = false;
      }
      // 草稿已转为正式记录，清除引用但不删除
      draftRef.current = null;
      setDraftVisitId(null);
      resetClose(true); loadRecords();
    } catch (err) { showToast('error', getErrorMessage(err)); } finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    if (!editRecordId) return;
    setSubmitting(true);
    try {
      // 编辑保存也要按当前表单内容重算阶段，避免停留在打开弹窗时加载的旧阶段
      const payload = { ...formData, record_phase: getPhase(formData) };
      await api.updateVisitRecord(editRecordId, payload);
      showToast('success', '更新成功'); setShowEditModal(false); setEditRecordId(null); loadRecords();
    } catch (err) { showToast('error', getErrorMessage(err)); } finally { setSubmitting(false); }
  };

  const handleView = async (id: number) => { try { const res = await api.getVisitRecord(id); setViewRecord(res.data); setShowViewModal(true); } catch (err) { showToast('error', getErrorMessage(err)); } };
  const handleEdit = async (id: number) => { try { const res = await api.getVisitRecord(id); const d = res.data as any; setFormData({ area: d.area || '', customer_name: d.customer_name || '', linkman:d.linkman || '', visit_time: d.visit_date || '', visit_purpose: d.visit_purpose || '', visit_result: d.visit_result || '', remark: d.remark || '', record_phase: d.record_phase ?? 0 }); setEditRecordId(id); setShowEditModal(true); } catch (err) { showToast('error', getErrorMessage(err)); } };
  const handleDelete = async (id: number) => { const ok = await confirmDialog.confirm({ title: '确认删除', message: '确定删除这条记录？', confirmText: '删除', danger: true }); if (!ok) return; try { await api.deleteVisitRecord(id); showToast('success', '已删除'); loadRecords(); } catch (err) { showToast('error', getErrorMessage(err)); } };
  const handleArchive = async (id: number, currentArchived: boolean) => {
    try {
      const res = await api.toggleArchive(id);
      showToast('success', res.data?.is_archived ? '已归档' : '已取消归档');
      loadRecords();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };
  const handleExport = async (id: number) => { try { await api.exportVisitReport(id); showToast('success', '报告已下载'); } catch (err) { showToast('error', getErrorMessage(err)); } };

  // 批量导出：仅阶段=完成 的记录可勾选导出
  const exportableRecords = useMemo(() => filteredRecords.filter(r => (r as any).record_phase === 2), [filteredRecords]);
  const allExportableSelected = exportableRecords.length > 0 && exportableRecords.every(r => selectedIds.has(r.visit_id));

  const toggleSelect = (id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const selectAllExportable = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIds(allExportableSelected ? new Set() : new Set(exportableRecords.map(r => r.visit_id)));
  };

  const handleBatchExport = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) { showToast('warning', '请先勾选要导出的记录'); return; }
    setExporting(true);
    try {
      await api.exportVisitReportsBatch(ids);
      showToast('success', `已下载 ${ids.length} 份报告`);
      setSelectedIds(new Set());
    } catch (err) {
      showToast('error', getErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };
  const handleAppendPost = async (id: number) => {
    // 补录事后：加载事前记录，切换到事后模式
    try {
      const res = await api.getVisitRecord(id);
      const d = res.data as any;
      setFormData({
        area: d.area || '', customer_name: d.customer_name || '',
        linkman: d.linkman || '', visit_time: d.visit_date || '', visit_purpose: d.visit_purpose || '',
        visit_result: d.visit_result || '',
        remark: d.remark || '', record_phase: 1,
      });
      setEditRecordId(id); // 用来标记更新已有记录
      draftRef.current = null; setDraftVisitId(null);  // 清除可能残留的草稿引用
      setShowInputModal(true); fetchHistories(id);  // 直接传 visit_id，不等 state 更新
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const createDraft = async () => {
    if (draftRef.current) return;  // 已有草稿，不重复创建
    try {
      const r = await api.request<any>('/visit-records/draft', {method:'POST'});
      draftRef.current = r.data?.visit_id || null;
      setDraftVisitId(draftRef.current);
    } catch (e) {
      console.error('创建草稿失败:', e);
      showToast('error', '创建草稿失败: ' + getErrorMessage(e));
    }
  };
  const deleteDraft = async (id?: number | null) => {
    const did = id ?? draftRef.current;
    if (!did) return;
    try { await api.request('/visit-records/draft/'+did, {method:'DELETE'}); } catch {}
    draftRef.current = null; setDraftVisitId(null);
  };
  const fetchHistories = async (ctxId?: number | null) => {
    try {
      const vid = ctxId ?? draftRef.current ?? editRecordId;
      const params = vid ? `?visit_id=${vid}` : '';
      const r = await api.request<any>('/visit-records/history' + params);
      setHistories((r.data || []).filter((item: any) => !deletedIdsRef.current.has(item.id)));
    } catch (e) { console.error('加载历史记录失败:', e); setHistories([]); }
  };

  const handleCloseInput = async () => {
    // 有未保存内容（文字输入 或 已有历史记录）→ 确认关闭
    const hasInput = transcript.trim();
    const hasExtracted = histories.length > 0;
    if (hasInput || hasExtracted) {
      const ok = await confirmDialog.confirm({
        title: '放弃编辑？',
        message: '有未保存的工作内容，关闭后将丢失。',
        confirmText: '放弃',
        danger: true,
      });
      if (!ok) return;
    }
    resetClose();
  };
  const resetClose = (skipDraftDelete?: boolean) => { setTranscript(''); setMissingFields([]); setHistories([]); setDeletedHistories([]); deletedIdsRef.current = new Set(); setFormData(emptyForm()); setShowInputModal(false); setShowConfirmModal(false); setEditRecordId(null);
    // 立即清除 ref，防止下次打开时 createDraft 因 ref 未清而跳过
    const did = draftRef.current;
    draftRef.current = null; setDraftVisitId(null);
    if (!skipDraftDelete && did) { deleteDraft(did); }
  };

  return (
    <div className={styles.container}>
      {/* ===== 页头 ===== */}
      <div className={styles.pageToolbar} style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
        <div style={{display:'flex',gap:4,background:'#f1f5f9',borderRadius:8,padding:3}}>
          <button onClick={()=>setFilterPhase('all')} style={{padding:'6px 16px',borderRadius:6,border:'none',cursor:'pointer',fontSize:'.8125rem',fontWeight:500,background:filterPhase==='all'?'#fff':'transparent',color:filterPhase==='all'?'var(--cinnabar-600)':'var(--ink-500)',boxShadow:filterPhase==='all'?'0 1px 3px rgba(0,0,0,.1)':'none'}}>未归档</button>
          <button onClick={()=>setFilterPhase('archived')} style={{padding:'6px 16px',borderRadius:6,border:'none',cursor:'pointer',fontSize:'.8125rem',fontWeight:500,background:filterPhase==='archived'?'#fff':'transparent',color:filterPhase==='archived'?'var(--cinnabar-600)':'var(--ink-500)',boxShadow:filterPhase==='archived'?'0 1px 3px rgba(0,0,0,.1)':'none'}}>已归档</button>
        </div>
        <div className={styles.pageToolbarActions} style={{display:'flex',gap:8,alignItems:'center'}}>
          <button
            className={styles.btnExport}
            onClick={handleBatchExport}
            disabled={selectedIds.size === 0 || exporting}
            title="勾选阶段为“完成”的记录后，批量下载出差报告（ZIP）"
            style={{ padding: '6px 12px', fontSize: '.75rem' }}
          >
            {exporting ? <Loader2 size={14} className={styles.spin} /> : <Download size={14} />}
            批量下载{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
          {selectedIds.size > 0 && (
            <button onClick={() => setSelectedIds(new Set())} style={{background:'#fff',border:'1px solid var(--color-border)',borderRadius:8,padding:'6px 10px',cursor:'pointer',fontSize:'.75rem',color:'var(--ink-500)'}}>
              取消选择
            </button>
          )}
          <button onClick={() => { setShowSettings(!showSettings); setShowMobileToolbar(false); }} style={{background:'#fff',border:'1px solid var(--color-border)',borderRadius:8,padding:'6px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontSize:'.75rem',color:'var(--ink-500)'}}>
            <Settings size={14} /> 设置
          </button>
          <button
            className={styles.mobileOnlySearchToggle}
            onClick={() => { setShowMobileToolbar(!showMobileToolbar); setShowSettings(false); }}
            style={{background:'#fff',border:'1px solid var(--color-border)',borderRadius:8,padding:'6px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontSize:'.75rem',color:'var(--ink-500)'}}
          >
            <Search size={14} /> 筛选
          </button>
        </div>
      </div>
      {/* 设置浮窗 */}
      {showSettings && (
        <div style={{position:'fixed',top:96,right:12,zIndex:200,background:'#fff',borderRadius:14,boxShadow:'0 12px 40px rgba(0,0,0,.15)',border:'1px solid var(--color-border)',padding:'18px 20px',width:280,fontSize:'.8125rem'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{width:32,height:32,borderRadius:8,background:'#fef2f2',display:'flex',alignItems:'center',justifyContent:'center'}}><Settings size={16} color='var(--cinnabar-500)'/></div>
              <span style={{fontWeight:600,fontSize:'.875rem'}}>提取字段设置</span>
            </div>
            <button onClick={()=>setShowSettings(false)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:4}}><X size={18}/></button>
          </div>
          <div style={{fontSize:'.6875rem',color:'var(--ink-400)',marginBottom:14,marginLeft:40}}>选择 AI 需要提取的关键信息字段</div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
          {FIELD_CONFIG.map(f => (
            <label key={f.key} onClick={() => setExtractFields(prev => { const n = new Set(prev); prev.has(f.key) ? n.delete(f.key) : n.add(f.key); return n; })} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',cursor:'pointer',borderRadius:8,transition:'background .15s',background:extractFields.has(f.key)?'#fef2f2':'transparent'}}>
              <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${extractFields.has(f.key)?'var(--cinnabar-500)':'#d1d5db'}`,'background':extractFields.has(f.key)?'var(--cinnabar-500)':'#fff',display:'flex',alignItems:'center',justifyContent:'center',transition:'all .15s'}}>
                {extractFields.has(f.key) && <Check size={12} color='#fff' strokeWidth={3}/>}
              </div>
              <span style={{fontSize:'.8125rem',color:extractFields.has(f.key)?'var(--ink-700)':'var(--ink-400)'}}>{f.label}</span>
            </label>
          ))}
          </div>
        </div>
      )}

      {/* ===== 搜索栏 ===== */}
      <div className={`${styles.searchBar} ${showMobileToolbar ? styles.searchBarVisible : ''}`}>
        <div className={styles.searchInput}>
          <Search size={18} strokeWidth={1.5} />
          <input type="text" placeholder="搜索拜访对象、联系人..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          {searchQuery && <button className={styles.clearSearch} onClick={() => setSearchQuery('')}><X size={16} /></button>}
        </div>
        <div className={styles.filters}>
          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>阶段</span>
            <select className={styles.filterSelect} value={filterPhase} onChange={e => setFilterPhase(e.target.value)}>
              <option value="all">全部阶段</option>
              <option value="0">草稿</option>
              <option value="1">事前</option>
              <option value="2">完成</option>
              <option value="archived">归档</option>
            </select>
          </div>
          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>工作人员</span>
            <select className={styles.filterSelect} value={filterSalesman} onChange={e => setFilterSalesman(e.target.value)}>
              <option value="all">全部人员</option>
              {salesmanList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>拜访日期</span>
            <input type="date" className={styles.dateInput} value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} />
            <span className={styles.dateSeparator}>至</span>
            <input type="date" className={styles.dateInput} value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} />
          </div>
          <button className={styles.clearFiltersBtn} disabled={!hasActiveFilters} onClick={resetFilters}>
            <RotateCcw size={14} /> 清除
          </button>
        </div>
      </div>

      {/* ===== 表格 ===== */}
      {loading ? (
        <div className={styles.loadingState}><Loader2 size={32} className={styles.spin} /></div>
      ) : (
        <div className={styles.tableContainer}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={`${styles.tableHeaderCell} ${styles.selectHeader}`} style={{ width: 40, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allExportableSelected}
                      disabled={exportableRecords.length === 0}
                      onChange={() => selectAllExportable()}
                      title={exportableRecords.length === 0 ? '没有可导出的已完成记录' : (allExportableSelected ? '取消全选' : '全选已完成记录')}
                    />
                  </th>
                  <th className={styles.tableHeaderCell} style={{ width: 95 }}>拜访日期</th>
                  <th className={styles.tableHeaderCell} style={{ width: 120 }}>拜访对象</th>
                  <th className={styles.tableHeaderCell} style={{ width: 65 }}>联系人</th>
                  <th className={styles.tableHeaderCell} style={{ width: 145 }}>拜访目的</th>
                  <th className={styles.tableHeaderCell} style={{ width: 130 }}>拜访结果</th>
                  <th className={styles.tableHeaderCell} style={{ width: 55 }}>阶段</th>
                  <th className={styles.tableHeaderCell} style={{ width: 65 }}>工作人员</th>
                  <th className={styles.tableHeaderCell} style={{ width: 120 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.length === 0 ? (
                  <tr><td colSpan={9} className={styles.emptyCell}>{hasActiveFilters ? '没有匹配的记录' : '暂无工作记录'}</td></tr>
                ) : paginatedRecords.map(r => {
                  const isExportable = (r as any).record_phase === 2;
                  return (
                    <tr key={r.visit_id} className={`${styles.tableRow} ${expandedCards.has(r.visit_id) ? styles.expanded : ''}`} onClick={() => toggleCard(r.visit_id)}
                    style={{background: r.is_archived ? '#f8fafc' : (r as any).record_phase === 2 ? '#f0fdf4' : (r as any).record_phase === 1 ? '#eff6ff' : '#fffbeb'}}>
                      <td className={`${styles.tableCell} ${styles.selectCell}`} style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.visit_id)}
                          disabled={!isExportable}
                          onChange={() => toggleSelect(r.visit_id)}
                          title={isExportable ? '选择导出' : '完成后才可导出'}
                        />
                      </td>
                      <td className={styles.tableCell} data-label="拜访日期"><span className={styles.cellValue}>{r.visit_date}</span></td>
                      <td className={styles.tableCell} style={{ fontWeight: 500 }} data-label="拜访对象"><span className={styles.cellValue}>{r.customer_name}</span></td>
                      <td className={styles.tableCell} data-label="联系人"><span className={styles.cellValue}>{r.linkman}</span></td>
                      <td className={styles.tableCell} data-label="拜访目的"><span className={styles.cellValue}>{r.visit_purpose}</span></td>
                      <td className={styles.tableCell} data-label="拜访结果"><span className={styles.cellValue}>{r.visit_result}</span></td>
                      <td className={styles.tableCell} data-label="阶段">
                        {r.is_archived ? (
                          <span style={{padding:'2px 8px',borderRadius:10,fontSize:'.75rem',fontWeight:600,background:'#f1f5f9',color:'#64748b'}}>归档</span>
                        ) : (
                          <span className={styles.phaseBadge} style={{
                            background: (r as any).record_phase === 2 ? '#dcfce7' : (r as any).record_phase === 1 ? '#dbeafe' : '#fef3c7',
                            color: (r as any).record_phase === 2 ? '#166534' : (r as any).record_phase === 1 ? '#1e40af' : '#92400e',
                            padding: '2px 8px', borderRadius: 10, fontSize: '.75rem', fontWeight: 600,
                          }}>{['草稿','事前','完成'][(r as any).record_phase ?? 0]}</span>
                        )}
                      </td>
                      <td className={styles.tableCell} data-label="工作人员">{r.salesman}</td>
                      <td className={styles.tableCell} style={{ textAlign: 'center' }} data-label="操作">
                        <div className={styles.actionBtns}>
                          <button className={styles.iconBtn} title="查看" onClick={() => handleView(r.visit_id)}><Eye size={15} /></button>
                          <button className={styles.iconBtn} title={r.is_archived ? '已归档，不可编辑' : r.user_id !== currentUserId && !isSuperAdmin ? "无权编辑" : "编辑"} disabled={!!r.is_archived || (r.user_id !== currentUserId && !isSuperAdmin)} onClick={() => handleEdit(r.visit_id)}><Edit3 size={15} /></button>
                          <button className={styles.iconBtn} title={(r as any).record_phase === 2 ? '导出报告' : '完成后才可导出'} disabled={(r as any).record_phase !== 2} onClick={() => handleExport(r.visit_id)}><Download size={15} /></button>
                          {(r.user_id === currentUserId || isSuperAdmin) && (
                            <button className={styles.iconBtn} title={r.is_archived ? '已归档，不可补录' : '补录'} style={{ color: r.is_archived ? 'var(--ink-300)' : 'var(--cinnabar-500)' }} disabled={!!r.is_archived} onClick={() => handleAppendPost(r.visit_id)}><FileText size={15} /></button>
                          )}
                          <button className={styles.iconBtn} title={r.is_archived ? '取消归档' : '归档'} onClick={() => handleArchive(r.visit_id, !!r.is_archived)}><Archive size={15} /></button>
                          <button className={`${styles.iconBtn} ${styles.dangerBtn}`} title={r.is_archived ? '已归档，不可删除' : r.user_id !== currentUserId && !isSuperAdmin ? "无权删除" : "删除"} disabled={!!r.is_archived || (r.user_id !== currentUserId && !isSuperAdmin)} onClick={() => handleDelete(r.visit_id)}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 总记录数 */}
      {!loading && <div style={{textAlign:'center',padding:'8px 0',fontSize:'.75rem',color:'var(--ink-400)'}}>共 {filteredRecords.length} 条记录</div>}


      {/* ===== 弹窗: 新增 ===== */}
      <AnimatePresence>{showInputModal && (
        <motion.div className={styles.modalOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleCloseInput}>
          <motion.div className={styles.modal} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editRecordId ? '补录工作记录' : '新增工作记录'}</h2>
              <button className={styles.modalClose} onClick={handleCloseInput}><X size={20} /></button>
            </div>
            <div className={styles.modalBody} style={{position:'relative',overflowY:'auto',display:'flex',flexDirection:'column',flex:1,minHeight:0}}>
              {/* 动态提示 — 基于当前表单数据 */}
              {(() => {
                const merged = formData;
                const phase = getPhase(merged);
                const extractedKeys = Object.keys(merged);
                if (phase === 0) {
                  const done = PRE_REQUIRED.filter(f => merged[f]);
                  const todo = PRE_REQUIRED.filter(f => !merged[f]);
                  return (
                    <div className={styles.missingHint}>
                      <p className={styles.missingTitle}>事前阶段 — 建议录入关键信息</p>
                      {done.length > 0 && <p className={styles.doneNote}>已录入：{done.map(f => REQUIRED_LABELS[f]).join('、')}</p>}
                      {todo.length > 0 && <p className={styles.missingFields}>建议录入：{todo.map(f => REQUIRED_LABELS[f]).join('、')}</p>}
                      {extractedKeys.length === 0 && <p className={styles.completeSub} style={{marginTop:4}}>输入描述后点击「AI 解析」逐轮提取信息</p>}
                    </div>
                  );
                }
                if (phase === 1) {
                  return (
                    <div className={styles.completeHint}>
                      <p className={styles.completeTitle}>事前阶段 ✓</p>
                      <p className={styles.completeGuide}>可继续录入事后关键信息：拜访结果</p>
                    </div>
                  );
                }
                // phase === 2
                return (
                  <div className={styles.completeHint}>
                    <p className={styles.completeTitle}>信息齐全</p>
                    <p className={styles.completeNote}>已提取 {extractedKeys.length} 个字段，可前往确认界面提交</p>
                  </div>
                );
              })()}
              <textarea className={styles.textarea}
                placeholder="语音转写或输入拜访描述..."
                value={transcript} onChange={e => setTranscript(e.target.value)} rows={6} />


              {/* 语音录入按钮（底部独立） */}
              {!isWechat && (
                <button className={`${styles.voiceBtn} ${isRecording ? styles.recording : ''}`} onClick={isRecording ? stopRecording : startRecording}>
                  {transcribing ? <><Loader2 size={24} className={styles.spin} /> AI 转写中...</> : extracting ? <><Loader2 size={24} className={styles.spin} /> AI 解析中...</> : isRecording ? <><MicOff size={24} /> 正在录音，点击结束</> : <><Mic size={24} /> 点击开始语音录入</>}
                </button>
              )}
              {isWechat && <p className={styles.wechatHint}>请在浏览器中打开此页面以使用语音录入功能</p>}
              {isRecording && <span className={styles.recordingHint}>录音中，点击按钮结束</span>}
              {histories !== null && (
                <div style={{marginTop:16,borderTop:'1px solid var(--ink-100)',paddingTop:12}}>
                  <div style={{fontSize:'.8125rem',fontWeight:600,marginBottom:8}}>历史记录</div>
                  <div style={{maxHeight:240,overflowY:'auto',paddingRight:4}}>
                  {histories.length === 0 ? <div style={{fontSize:'.75rem',color:'var(--ink-300)',textAlign:'center',padding:8}}>暂无历史记录</div> :
                  histories.map((h:any) => {
                    const fullPreview = (h.full_text || '').slice(0, 50);
                    return (
                    <div key={h.id}
                      style={{padding:'8px 12px',marginBottom:4,background:'#f9fafb',borderRadius:8,fontSize:'.75rem',color:'var(--ink-600)',display:'flex',alignItems:'center',gap:8}}>
                      <div onClick={()=>setViewHistory(h)} style={{flex:1,cursor:'pointer',minWidth:0}}>
                        <div style={{fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{h.summary||'记录 '+h.id}</div>
                        {fullPreview && <div style={{fontSize:'.6875rem',color:'var(--ink-400)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fullPreview}{fullPreview.length >= 50 ? '…' : ''}</div>}
                        <div style={{fontSize:'.625rem',color:'var(--ink-400)',marginTop:1}}>{new Date(h.create_time).toLocaleString('zh-CN')}</div>
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const ok = await confirmDialog.confirm({ title: '删除历史记录', message: '确定删除此条记录？提交前可恢复。', confirmText: '删除', danger: true });
                          if (!ok) return;
                          deletedIdsRef.current = new Set([...deletedIdsRef.current, h.id]);
                          setDeletedHistories(prev => [...prev, h]);
                          setHistories(prev => prev.filter(x => x.id !== h.id));
                        }}
                        style={{background:'none',border:'none',cursor:'pointer',padding:4,color:'var(--ink-300)',flexShrink:0}}
                        title="删除此条记录"
                      ><Trash2 size={14}/></button>
                    </div>
                    );
                  })}
                  </div>
                </div>
              )}

              {/* 历史记录详情面板 — 桌面端右侧弹出，移动端底部弹出 */}
              <AnimatePresence>{viewHistory && (
                <motion.div className={styles.historyDetailOverlay}
                  initial={{x:'100%'}} animate={{x:0}} exit={{x:'100%'}} transition={{type:'spring',damping:25,stiffness:200}}>
                  <div className={styles.historyDetailHeader}>
                    <div style={{fontWeight:600,fontSize:'.875rem'}}>录入详情</div>
                    <button onClick={()=>setViewHistory(null)} style={{background:'none',border:'none',cursor:'pointer',padding:4,color:'var(--ink-400)'}}><X size={18}/></button>
                  </div>
                  <div className={styles.historyDetailBody}>
                    <div style={{marginBottom:16}}>
                      <div style={{fontSize:'.75rem',fontWeight:600,color:'var(--ink-400)',marginBottom:6}}>完整输入文本</div>
                      <div className={styles.historyInputPreview}>{viewHistory.full_text || '无'}</div>
                    </div>
                    <div>
                      <div style={{fontSize:'.75rem',fontWeight:600,color:'var(--ink-400)',marginBottom:6}}>AI 提取关键信息</div>
                      {viewHistory.raw_data && Object.keys(viewHistory.raw_data).length > 0 ? (
                        <div className={styles.historyExtractPreview}>
                          {Object.entries(viewHistory.raw_data).filter(([,v]:any)=>v!==null&&v!=='').map(([k,v]:any)=>(
                            <div key={k} className={styles.historyFieldRow}>
                              <span className={styles.historyFieldLabel}>{FIELD_CONFIG.find(f=>f.key===k)?.label||k}</span>
                              <span className={styles.historyFieldValue}>{typeof v==='string'?v:JSON.stringify(v)}</span>
                            </div>
                          ))}
                        </div>
                      ) : <div style={{fontSize:'.75rem',color:'var(--ink-300)'}}>无提取信息</div>}
                    </div>
                  </div>
                  <div className={styles.historyDetailFooter}>
                    {new Date(viewHistory.create_time).toLocaleString('zh-CN')}
                  </div>
                </motion.div>
              )}</AnimatePresence>

            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* ===== 弹窗: 确认 ===== */}
      <AnimatePresence>{showConfirmModal && (
        <motion.div className={styles.modalOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => resetClose()}>
          <motion.div className={styles.modalLarge} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}><h2>确认拜访信息</h2><button className={styles.modalClose} onClick={() => resetClose()}><X size={20} /></button></div>
            <div className={styles.modalBody}><FormFields data={formData} onChange={(k, v) => setFormData(prev => ({ ...prev, [k]: v }))} /></div>
            <div className={styles.modalFooter}>
              <button className={styles.btnCancel} onClick={() => resetClose()}>取消</button>
              <button className={styles.btnBack} onClick={() => { setShowConfirmModal(false); setShowInputModal(true); fetchHistories(); }}>继续录入</button>
              <button className={styles.btnSubmit} disabled={submitting} onClick={handleSubmit}>{submitting ? <><Loader2 size={16} className={styles.spin} /> 提交中...</> : <><Save size={16} /> 确认提交</>}</button>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* ===== 弹窗: 查看 ===== */}
      <AnimatePresence>{showViewModal && viewRecord && (
        <motion.div className={styles.modalOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowViewModal(false)}>
          <motion.div className={styles.modalLarge} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}><h2>拜访记录详情</h2><span className={styles.modalSubInfo}>{viewRecord.visit_date} | {viewRecord.salesman}</span><div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>{(viewRecord as any).record_phase === 2 && (
              <button className={styles.btnExport} onClick={() => handleExport(viewRecord.visit_id)}><Download size={16} /> 导出报告</button>
            )}<button className={styles.modalClose} onClick={() => setShowViewModal(false)} style={{ marginLeft: 0 }}><X size={20} /></button></div></div>
            <div className={styles.modalBody}><FormFields data={viewRecord} readOnly /></div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* ===== 弹窗: 编辑 ===== */}
      <AnimatePresence>{showEditModal && (
        <motion.div className={styles.modalOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setShowEditModal(false); setEditRecordId(null); }}>
          <motion.div className={styles.modalLarge} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}><h2>编辑工作记录</h2><button className={styles.modalClose} onClick={() => { setShowEditModal(false); setEditRecordId(null); }}><X size={20} /></button></div>
            <div className={styles.modalBody}><FormFields data={formData} onChange={(k, v) => setFormData(prev => ({ ...prev, [k]: v }))} /></div>
            <div className={styles.modalFooter}><button className={styles.btnCancel} onClick={() => { setShowEditModal(false); setEditRecordId(null); }}>取消</button><button className={styles.btnSubmit} disabled={submitting} onClick={handleUpdate}>{submitting ? <><Loader2 size={16} className={styles.spin} /> 保存中...</> : <><Save size={16} /> 保存修改</>}</button></div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>
      {/* 浮动新增按钮 */}
      {expandedCards.size === 0 && (
      <motion.button className={styles.fab} onClick={async () => { setTranscript(''); setHistories([]); setDeletedHistories([]); deletedIdsRef.current = new Set(); setMissingFields([]); setEditRecordId(null); draftRef.current = null; setDraftVisitId(null); setShowInputModal(true); await createDraft(); if (draftRef.current) fetchHistories(); }} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
        <Plus size={28} strokeWidth={2.5} />
      </motion.button>
      )}
    </div>
  );
}
